import type { NotificationPermissionStatus } from "../../../services/notificationPermissionStatus";

/**
 * What this device should do about the push permission it just read.
 *
 *  - `say-nothing` — no account write, no memory change.
 *  - `remember` — note what this device now reads, but tell the account
 *    nothing. This is how a device learns its own starting point without
 *    speaking for the reader.
 *  - `record` — write it to the account, then remember it.
 */
export type PushPermissionAction =
  | { kind: "say-nothing" }
  | { kind: "remember"; status: NotificationPermissionStatus }
  | {
      kind: "record";
      status: NotificationPermissionStatus;
      pushOptOut: boolean;
    };

/**
 * `User.pushOptOut` is one flag for the whole account, on purpose: a reader who
 * turns push off on one device has asked to stop receiving push, not to stop
 * receiving it there. The choice travels with the reader, and every other
 * device must honour it.
 *
 * That is what makes writing it narrow. The flag gates delivery
 * (`pushOptedInFilter`, `notifyUser`, `send-push`), so anything written without
 * the reader having answered either silences them or overrules a choice they
 * already made. One thing may write it:
 *
 *  - **a CHANGE this device witnesses**, in either direction. The permission
 *    was one thing when we last looked and is another now, so the reader acted
 *    — at the OS prompt, or in the system settings.
 *
 * Every other reading is ambient: it says what this device happens to be set
 * to, not what the reader just decided.
 *
 * So a first sighting is `remember`, never `record`. A device that has never
 * reported cannot tell whether its permission is news; it only knows what it is
 * currently set to. Recording that would write the account from a reading
 * nobody made:
 *
 *  - every browser starts with an empty memory, so the first foreground after
 *    deploy would re-assert `denied` from any desktop that happens to be
 *    denied, and re-assert `authorized` from any phone that happens to be
 *    authorized — the second of which CLEARS a refusal the reader made
 *    deliberately somewhere else;
 *  - losing site storage would do the same, repeatedly, in both directions.
 *
 * `notDetermined` is likewise never recorded, in any position: nobody has been
 * asked yet. It is remembered, so that answering the prompt afterwards reads as
 * the change it is. (It used to fall on the `false` side of
 * `status === "denied"` and assert an opt-in the reader never gave.)
 *
 * An unreadable permission — `observed === null`, a failed native bridge call —
 * is not even remembered. The caller reads with
 * `readOsNotificationPermission`, never `getNotificationPermissionState`, which
 * coerces that failure to `"denied"`; that coercion is right for deciding
 * whether to show an in-app prompt and disastrous to store, and it used to
 * write `pushOptOut: true` for a whole account on one flaky call.
 *
 * A reader whose account is opted out, and whose every device already reads
 * `authorized`, therefore has no transition left for this path to witness. That
 * is on purpose, and it is why the settings switch states the decision directly
 * instead of mirroring the OS: `pushSwitchAction` writes the account once the
 * OS is out of the way, which is the way back, and the only way to opt out
 * without revoking the OS permission.
 */
export function pushPermissionAnswer(
  observed: NotificationPermissionStatus | null,
  lastReported: NotificationPermissionStatus | null
): PushPermissionAction {
  if (observed === null) return { kind: "say-nothing" };
  if (observed === lastReported) return { kind: "say-nothing" };
  if (observed === "notDetermined" || lastReported === null)
    return { kind: "remember", status: observed };
  return {
    kind: "record",
    status: observed,
    pushOptOut: observed === "denied",
  };
}
